/**
 * The live odds panel inside the reveal — Pass 06 §5a.
 *
 * DISPLAY ONLY, and the module it reads from is built so that it cannot be anything else. The draw
 * is complete and correctly weighted before a single row lights; `revealOdds` takes the prior and
 * the REVEALED ROWS and never the stored result, so there is nothing here for a number to leak out
 * of. `revealOdds.test.ts` fixes two different results behind one revealed prefix and asserts the
 * output is identical.
 *
 * ONE QUESTION, THROUGHOUT: the chance of choosing first (Tim, 25 August). The panel used to
 * open on "still alive for a top-four pick" and swap once the field was fixed; see `questionFor`
 * for what that bought and why it was given up. The heading still names the question, because a
 * percentage with no question attached is a number people will read as the wrong one.
 *
 * ENUMERATED ONCE. `enumerateOutcomes` walks 5,040 ordered draws; it depends only on the prior, so
 * it is computed when the reveal mounts and handed back in on every step.
 */

import { useMemo } from 'react';
import { badgeMark, managerShort } from '../domain/identity.js';
import { Mark } from './avatars.js';
import {
  revealOdds, type Outcome, type Revealed, type Weighted,
} from '../domain/revealOdds.js';

interface Props {
  /** The prior — every GM with the weight his place carries. */
  all: readonly Weighted[];
  /** What the room has seen, index 0 = board position 1. */
  revealed: Revealed;
  /** Enumerated once by the reveal and passed down. */
  outcomes: readonly Outcome[];
  me: string;
  /**
   * Placed by 2020-II-1 §3 rather than drawn, and the board cannot be modelled without him — the
   * second choosing position is his whatever the lottery did, so reading that row as a draw
   * result rules out every enumerated outcome. See the note on `boardFor`.
   */
  toiletBowl?: string | null;
}

export function RevealOddsPanel({ all, revealed, outcomes, me, toiletBowl = null }: Props) {
  const odds = useMemo(
    () => revealOdds(all, revealed, outcomes, toiletBowl),
    [all, revealed, outcomes, toiletBowl],
  );
  const up = me.toUpperCase();

  /* Nothing left to be uncertain about — the board is full and the panel has said its piece. */
  if (odds.rows.length === 0) return null;

  /*
   * THE LAST NAME IS NOT A SUSPENSE NUMBER (§5c). Once position 2 lands, three of the four are
   * placed and the fourth is forced — the panel would read a triumphant 100% for a fact the room
   * worked out a beat earlier. It says what it actually is instead.
   */
  const forced = odds.rows.length === 1;
  /* 17k: the panel foot — the viewer's own chance, recomputed over the undrawn pool. */
  const own = odds.rows.find((r) => r.gm.toUpperCase() === up);
  const ownPct = own ? Math.round(own.p * 1000) / 10 : null;

  return (
    <section className="dolodp" aria-live="polite">
      {!forced && (
        <p className="dolodpown">
          Your chance of choosing first · <b>
            {ownPct === null ? 'placed' : own!.p > 0 && ownPct < 0.1 ? '<0.1%' : `${ownPct}%`}
          </b>
          <span className="dolodpownsub">
            Recomputed over the {odds.rows.length} undrawn weights as rows land. Display only.
          </span>
        </p>
      )}
      <div className="dolodphd">
        <span className="dolodpq">
          {forced ? 'The last name' : 'Chance of choosing first'}
        </span>
        {!forced && (
          <span className="dolodpn">{odds.rows.length} left</span>
        )}
      </div>

      <ul className="dolodplist">
        {odds.rows.map((r) => {
          const mine = r.gm.toUpperCase() === up;
          const pct = Math.round(r.p * 1000) / 10;
          return (
            <li key={r.gm} className={mine ? 'mine' : ''}>
              <Mark gm={r.gm} className="dolodpmark">{badgeMark(r.gm)}</Mark>
              <span className="dolodpname">{managerShort(r.gm)}</span>
              {forced ? (
                <span className="dolodpforced">forced</span>
              ) : (
                <>
                  <span className="dolodptrack" aria-hidden="true">
                    <i style={{ width: `${Math.max(2, r.p * 100)}%` }} />
                  </span>
                  {/*
                    ONE DECIMAL, and it earns it: through the chalk tail these move by fractions,
                    and a panel rounding to whole numbers would sit visibly still for six reveals.
                    Under 0.1% reads "<0.1%" rather than a 0.0 that looks like elimination — a GM
                    who is still alive should never see a zero.
                  */}
                  <span className="dolodppct">
                    {r.p > 0 && pct < 0.1 ? '<0.1%' : `${pct}%`}
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
